/* ===========================================================================
   Editorial Cart — intercepts add-to-cart, fetches cart state, updates drawer
   =========================================================================== */
(() => {
  'use strict';

  const drawer = document.querySelector('[data-ed-cart-drawer]');
  if (!drawer) return;

  const itemsEl = drawer.querySelector('[data-cart-items]');
  const emptyEl = drawer.querySelector('[data-cart-empty]');
  const footEl = drawer.querySelector('[data-cart-foot]');
  const countEl = drawer.querySelector('[data-cart-count]');
  const subtotalEl = drawer.querySelector('[data-cart-subtotal]');

  const shop = window.EditorialShop || { currency: 'COP', locale: 'es-CO' };
  const fmt = new Intl.NumberFormat(shop.locale || 'es-CO', {
    style: 'currency',
    currency: shop.currency || 'COP',
    maximumFractionDigits: 0,
  });
  const formatMoney = (cents) => fmt.format((cents || 0) / 100);

  /* ---------- open / close ---------- */
  const open = () => {
    drawer.hidden = false;
    requestAnimationFrame(() => drawer.classList.add('is-open'));
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${window.innerWidth - document.documentElement.clientWidth}px`;
  };
  const close = () => {
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
    setTimeout(() => { drawer.hidden = true; }, 450);
  };

  /* ---------- render ---------- */
  const render = (cart) => {
    const count = cart.item_count || 0;
    if (countEl) countEl.textContent = `${count} ${count === 1 ? 'producto' : 'productos'}`;

    if (count === 0) {
      if (itemsEl) itemsEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      if (footEl) footEl.hidden = true;
    } else {
      if (emptyEl) emptyEl.hidden = true;
      if (footEl) footEl.hidden = false;
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
      if (itemsEl) {
        itemsEl.innerHTML = cart.items
          .map((item) => {
            const image = item.image
              ? `<img src="${item.image}" alt="${escapeAttr(item.product_title)}" loading="lazy">`
              : '';
            const variantLine = item.variant_title && item.variant_title !== 'Default Title'
              ? `<span class="ed-cart__item-variant">${escapeHtml(item.variant_title)}</span>`
              : '';
            return `
              <li class="ed-cart__item" data-key="${escapeAttr(item.key)}">
                <a href="${escapeAttr(item.url)}" class="ed-cart__item-media">${image}</a>
                <div class="ed-cart__item-info">
                  <a href="${escapeAttr(item.url)}" class="ed-cart__item-title">${escapeHtml(item.product_title)}</a>
                  ${variantLine}
                  <div class="ed-cart__item-actions">
                    <div class="ed-cart__item-qty">
                      <button type="button" data-qty="${escapeAttr(item.key)}" data-delta="-1" aria-label="Restar">−</button>
                      <span>${item.quantity}</span>
                      <button type="button" data-qty="${escapeAttr(item.key)}" data-delta="1" aria-label="Sumar">+</button>
                    </div>
                    <span class="ed-cart__item-price">${formatMoney(item.final_line_price)}</span>
                  </div>
                </div>
                <button type="button" class="ed-cart__item-remove" data-remove="${escapeAttr(item.key)}" aria-label="Eliminar ${escapeAttr(item.product_title)}">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
                </button>
              </li>
            `;
          })
          .join('');
      }
    }

    updateHeaderBadge(count);

    // Disable recommended items that are already in cart
    const inCartIds = new Set(cart.items.map((i) => String(i.product_id)));
    drawer.querySelectorAll('[data-rec-id]').forEach((el) => {
      el.dataset.inCart = inCartIds.has(el.dataset.recId) ? 'true' : 'false';
    });
  };

  const updateHeaderBadge = (count) => {
    const badges = document.querySelectorAll('.ed-hdr__cart-badge');
    badges.forEach((b) => {
      if (count > 0) {
        b.textContent = count;
        b.hidden = false;
      } else {
        b.hidden = true;
      }
    });
    // If no badge exists yet, create one
    if (badges.length === 0 && count > 0) {
      const cartBtn = document.querySelector('.ed-hdr__cart');
      if (cartBtn) {
        const badge = document.createElement('span');
        badge.className = 'ed-hdr__cart-badge';
        badge.textContent = count;
        cartBtn.appendChild(badge);
      }
    }
  };

  /* ---------- API ---------- */
  const fetchCart = async () => {
    try {
      const res = await fetch(`${shop.cartUrl || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      const cart = await res.json();
      render(cart);
      return cart;
    } catch (err) {
      console.error('[ed-cart] fetch failed', err);
    }
  };

  const addToCart = async (formData) => {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      body: formData,
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.description || err.message || 'Error al agregar al carrito');
    }
    return res.json();
  };

  const updateItemQty = async (key, quantity) => {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    return res.json();
  };

  /* ---------- Global event listeners ---------- */
  document.addEventListener('click', (e) => {
    // Open triggers
    const openBtn = e.target.closest('[data-ed-cart-open]');
    if (openBtn) {
      e.preventDefault();
      fetchCart().then(open);
      return;
    }
    // Close triggers
    const closeBtn = e.target.closest('[data-ed-cart-close]');
    if (closeBtn) {
      e.preventDefault();
      close();
      return;
    }
    // Qty buttons inside drawer
    const qtyBtn = e.target.closest('[data-qty]');
    if (qtyBtn) {
      const key = qtyBtn.dataset.qty;
      const delta = parseInt(qtyBtn.dataset.delta, 10);
      const itemEl = qtyBtn.closest('.ed-cart__item');
      const qtySpan = itemEl?.querySelector('.ed-cart__item-qty span');
      const current = parseInt(qtySpan?.textContent || '1', 10);
      const next = Math.max(0, current + delta);
      drawer.classList.add('ed-is-loading');
      updateItemQty(key, next).then((cart) => {
        render(cart);
        drawer.classList.remove('ed-is-loading');
      });
      return;
    }
    // Remove button
    const removeBtn = e.target.closest('[data-remove]');
    if (removeBtn) {
      const key = removeBtn.dataset.remove;
      drawer.classList.add('ed-is-loading');
      updateItemQty(key, 0).then((cart) => {
        render(cart);
        drawer.classList.remove('ed-is-loading');
      });
      return;
    }
  });

  // Escape to close
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  // Intercept ALL add-to-cart form submits
  document.addEventListener(
    'submit',
    async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const action = form.getAttribute('action') || '';
      if (!/\/cart\/add(\?|$)/.test(action)) return;

      e.preventDefault();
      const btn = form.querySelector('button[type="submit"], input[type="submit"]');
      if (btn) {
        btn.dataset.edOriginalText = btn.textContent;
        btn.disabled = true;
        const textSpan = btn.querySelector('[data-atc-text]');
        if (textSpan) textSpan.textContent = 'Agregando…';
      }

      try {
        await addToCart(new FormData(form));
        await fetchCart();
        open();
      } catch (err) {
        console.error('[ed-cart] add failed', err);
        alert(err.message || 'No se pudo agregar al carrito');
      } finally {
        if (btn) {
          btn.disabled = false;
          const textSpan = btn.querySelector('[data-atc-text]');
          if (textSpan) textSpan.textContent = btn.dataset.edOriginalText || 'Agregar al carrito';
        }
      }
    },
    true
  );

  /* ---------- utils ---------- */
  function escapeHtml(str) {
    return String(str || '').replace(/[&<>"']/g, (s) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[s]));
  }
  function escapeAttr(str) { return escapeHtml(str); }

  /* ---------- initial load ---------- */
  fetchCart();
})();
